import type {
  GroupResponsibleState,
  Participant,
  Room,
  RoomFilterType,
} from '@/scopes/account/group-responsible/domain/types'

function sortRooms(rooms: Room[], participants: Participant[]): Room[] {
  return rooms.sort((a, b) => {
    const aHasAssigned = roomHasAssignedParticipants(a, participants)
    const bHasAssigned = roomHasAssignedParticipants(b, participants)

    if (aHasAssigned !== bHasAssigned) {
      return aHasAssigned ? -1 : 1
    }

    return a.name.localeCompare(b.name)
  })
}

function roomHasAssignedParticipants(room: Room, participants: Participant[]): boolean {
  return room.participantsId.some(participantId =>
    participants.some(participant => Number(participant.id) === participantId && participant.assigned),
  )
}

export function getRoomExtraInfo(state: GroupResponsibleState) {
  const activeRoom = state.activeExtraInfoId ? state.rooms[state.activeExtraInfoId] : null

  return {
    name: activeRoom?.name || '',
    participants: activeRoom?.participants || [],
    type: activeRoom?.type || '-',
    accommodationName: activeRoom?.accommodationName || '-',
  }
}

export function getRoomsOccupiedCounts(state: GroupResponsibleState): {
  all: number
  notOccupied: number
  occupied: number
} {
  const { rooms } = state
  if (!rooms)
    return { all: 0, notOccupied: 0, occupied: 0 }

  const all = Object.keys(rooms).length

  const notOccupied = Object.keys(rooms).filter(
    room => rooms[room].occupancy.occupied !== rooms[room].occupancy.max,
  ).length

  const occupied = all - notOccupied

  return { all, notOccupied, occupied }
}

export function getFilteredRoomsOccupied(state: GroupResponsibleState, filter: RoomFilterType, searchTerm = '') {
  const rooms = Object.values(state.rooms)
  const participants = Object.values(state.participants)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  let filteredRooms = rooms

  if (filter !== 'all') {
    filteredRooms = rooms.filter((room) => {
      const { occupied, max } = room.occupancy
      return filter === 'occupied' ? occupied === max : occupied !== max
    })
  }

  if (trimmedSearchTerm) {
    filteredRooms = filteredRooms.filter((room) => {
      const nameMatches = room.name.toLowerCase().includes(trimmedSearchTerm)
      if (nameMatches)
        return true

      const roomParticipants = participants.filter((participant) => {
        return room.participantsId.includes(Number(participant.id))
      })

      return roomParticipants.some(participant => participant.name.toLowerCase().includes(trimmedSearchTerm))
    })
  }

  return sortRooms(filteredRooms, participants)
}
